import { AssessmentAnswers, RecommendationResult } from './types';
import {
  USD_FALLBACK_PLANS,
  UsdPlan,
  formatUsd,
} from '../../../services/usdPricingModel';

type TierName = RecommendationResult['recommendedTier'];

const TIER_ID_BY_NAME: Record<TierName, string> = {
  Free: 'freemium',
  Essentials: 'essentials',
  Professional: 'professional',
  Enterprise: 'enterprise',
};

const NAME_BY_TIER_ID: Record<string, TierName> = {
  freemium: 'Free',
  essentials: 'Essentials',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const TIER_RANK: Record<string, number> = {
  freemium: 0,
  essentials: 1,
  professional: 2,
  enterprise: 3,
};

const unitCapPhrase = (plan: UsdPlan): string =>
  plan.maxUnits >= 9999
    ? 'your entire portfolio'
    : `up to ${plan.maxUnits} unit${plan.maxUnits === 1 ? '' : 's'}`;

const pricePhrase = (plan: UsdPlan): string => {
  if (plan.contactSales) return 'sales-led pricing';
  if (plan.price === 0) return 'free';
  return `${formatUsd(plan.price)}/mo`;
};

const allowancePhrase = (plan: UsdPlan): string | null => {
  const n = plan.serviceCallAllowance;
  return typeof n === 'number' && n > 0 && n < 9999
    ? `${n} service calls/month included`
    : null;
};

export const evaluateNeeds = (
  answers: AssessmentAnswers,
  plans: UsdPlan[] = USD_FALLBACK_PLANS,
): RecommendationResult => {
  const { propertyCount: rawCount, serviceNeeds, landlordProfile } = answers;

  let recommendedTier: RecommendationResult['recommendedTier'] = 'Free';
  const reasoning: string[] = [];
  let confidenceScore = 100;

  const propertyCount = rawCount === '' ? 0 : Number(rawCount);

  if (!landlordProfile || rawCount === '') {
    return { recommendedTier: 'Free', confidenceScore: 0, reasoning: ['Incomplete data'] };
  }

  if (landlordProfile === 'manager' || propertyCount >= 50) {
    recommendedTier = 'Enterprise';
    if (landlordProfile === 'manager') {
      reasoning.push('Property managers benefit from our Enterprise portfolio tools.');
    }
    if (propertyCount >= 50) {
      reasoning.push(`Managing ${propertyCount} units requires Enterprise-grade scalability.`);
    }
  } else if (propertyCount >= 10 || serviceNeeds.includes('eviction') || serviceNeeds.length >= 3) {
    recommendedTier = 'Professional';
    if (propertyCount >= 10) {
      reasoning.push('Portfolios of 10+ units are best served by the Professional tier.');
    }
    if (serviceNeeds.includes('eviction')) {
      reasoning.push('Eviction support is included in the Professional tier.');
    }
    if (serviceNeeds.length >= 3) {
      reasoning.push('Broad compliance needs are fully covered by Professional.');
    }
  } else if (serviceNeeds.length > 0 || propertyCount >= 2) {
    recommendedTier = 'Essentials';
    reasoning.push('Essentials covers basic multi-property or specific compliance requirements.');
  } else {
    recommendedTier = 'Free';
    reasoning.push('The Free tier is perfect for single-property owners getting started.');
  }

  const basePlanId = TIER_ID_BY_NAME[recommendedTier];
  const basePlan = plans.find((p) => p.id === basePlanId);
  if (basePlan && basePlan.maxUnits < propertyCount) {
    const baseRank = TIER_RANK[basePlanId] ?? 0;
    const fitting = [...plans]
      .filter((p) => (TIER_RANK[p.id] ?? 99) >= baseRank && p.maxUnits >= propertyCount)
      .sort((a, b) => (TIER_RANK[a.id] ?? 99) - (TIER_RANK[b.id] ?? 99))[0];
    const next = fitting ? NAME_BY_TIER_ID[fitting.id] : undefined;
    if (fitting && next && fitting.id !== basePlan.id) {
      recommendedTier = next;
      const allow = allowancePhrase(fitting);
      reasoning.push(
        `${basePlan.name} covers ${unitCapPhrase(basePlan)} — ` +
          `${fitting.name} (${unitCapPhrase(fitting)}, ${pricePhrase(fitting)}` +
          `${allow ? `, ${allow}` : ''}) fits your ${propertyCount} properties.`,
      );
    }
  }

  if (serviceNeeds.length === 0 && propertyCount > 1) {
    confidenceScore -= 10;
    reasoning.push('Confidence adjusted based primarily on portfolio size as no specific services were selected.');
  }

  if (propertyCount === 0 && serviceNeeds.length > 0) {
    confidenceScore -= 20;
    reasoning.push('Confidence reduced due to 0 properties reported.');
  }

  return { recommendedTier, confidenceScore, reasoning };
};
